import { type ButtonHTMLAttributes, memo } from "react";

import { cn } from "@/lib/utils/cn";
import type { Intent, Size } from "@/types/ui";

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  intent?: Intent;
  size?: Size;
  loading?: boolean;
  fullWidth?: boolean;
}

const intentClasses: Record<Intent, string> = {
  primary:
    "bg-sidebar-bg text-white hover:bg-sidebar-bg focus-visible:ring-primary-500",
  // Brand-colored secondary: subtle primary tint instead of the old
  // success/green placeholder, so it reads as "secondary action" rather
  // than "positive/confirm action".
  secondary:
    "bg-primary-50 text-primary-700 hover:bg-primary-100 focus-visible:ring-primary-500 dark:bg-primary-900/20 dark:text-primary-300 dark:hover:bg-primary-900/30",
  success:
    "bg-green-500 text-white hover:bg-green-600 focus-visible:ring-green-500",
  warning:
    "bg-amber-500 text-white hover:bg-amber-600 focus-visible:ring-amber-500",
  danger: "bg-red-500 text-white hover:bg-red-600 focus-visible:ring-red-500",
  ghost:
    "bg-transparent text-neutral-600 hover:bg-neutral-100 dark:text-neutral-400 dark:hover:bg-neutral-800",
};

const sizeClasses: Record<Size, string> = {
  xs: "h-6  px-2   text-xs  rounded",
  sm: "h-8  px-3   text-sm  rounded-md",
  md: "h-9  px-4   text-sm  rounded-lg",
  lg: "h-10 px-5   text-base rounded-lg",
  xl: "h-12 px-6   text-base rounded-xl",
};

/**
 * Base button component.
 * Usage:
 *   <Button intent="primary" size="md" onClick={handleSave}>Save</Button>
 *   <Button intent="danger" loading={isDeleting}>Delete</Button>
 */
const Button = memo(function Button({
  intent = "primary",
  size = "md",
  loading = false,
  fullWidth = false,
  disabled,
  children,
  className,
  ...props
}: ButtonProps) {
  return (
    <button
      disabled={disabled ?? loading}
      aria-busy={loading}
      className={cn(
        // Base
        "inline-flex items-center justify-center gap-2 font-medium",
        "transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
        "disabled:cursor-not-allowed disabled:opacity-50",
        // Variants
        intentClasses[intent],
        sizeClasses[size],
        fullWidth && "w-full",
        className,
      )}
      {...props}
    >
      {loading && (
        <span
          aria-hidden
          className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent"
        />
      )}
      {children}
    </button>
  );
});

export default Button;
